'use client'

import _ from "underscore"
import moment from "moment"
import confetti from "canvas-confetti"

// Regex pour détecter une vCard
const VCARD_REGEX = /^BEGIN:VCARD\s*\r?\n[\s\S]*?\r?\nEND:VCARD\s*$/mi

const mimeTypesSignatures = {
  Qk02U: 'image/bmp',
  '/9j/': 'image/jpg',
  R0lGODdh: 'image/gif',
  R0lGODlh: 'image/gif',
  iVBORw0KGgo: 'image/png',
  JVBERi0: 'application/pdf',
}

/**
 * Group messages/conversations by period
 */
export function groupAndFlattenItems(items: any[], locale: string) {
  const isEnglish = /en/gi.test(locale)
  moment.locale(locale ?? "en")
  const now = moment()
  const sevenDaysAgo = now.clone().subtract(7, "days")
  const thirtyDaysAgo = now.clone().subtract(30, "days")

  const todayLabel = () => isEnglish ? "Today" : "Aujourd'hui"
  const sevenDaysAgoLabel = () => isEnglish ? "7 past days" : "7 jours précédents"
  const thirtyDaysAgoLabel = () => isEnglish ? "30 past days" : "30 jours précédents"

  const grouped = _.groupBy(items, (item) => {
    const itemDate = moment(item.lastAccessed)
    if (itemDate.isSame(now, "day")) return todayLabel()
    if (itemDate.isAfter(sevenDaysAgo)) return sevenDaysAgoLabel()
    if (itemDate.isAfter(thirtyDaysAgo)) return thirtyDaysAgoLabel()
    if (itemDate.year() === now.year()) return itemDate.format("MMMM")
    return itemDate.format("YYYY")
  })

  const sortedGroups = _.sortBy(
    _.map(grouped, (titles, period) => ({
      period,
      titles,
      latest: _.max(titles, (item: any) => moment(item.lastAccessed).valueOf())
    })),
    (group) => -moment(group.latest.lastAccessed).valueOf()
  )

  return _.flatten(_.map(sortedGroups, (group) => [
    { kind: "header", title: group.period },
    ...group.titles.map((item: any) => ({ segment: item.segment, title: item.title, userId: item.userId }))
  ]))
}

const fire = (particleRatio: number, opts: {
  spread: number
  startVelocity?: number
  decay?: number
  scalar?: number
}) => {
  const defaults = { origin: { y: 0.7 } }
  confetti({
    ...defaults,
    ...opts,
    particleCount: Math.floor(200 * particleRatio)
  })
}

export const showConfetti = () => {
  fire(0.25, { spread: 26, startVelocity: 55 })
  fire(0.2, { spread: 60 })
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 })
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 })
  fire(0.1, { spread: 120, startVelocity: 45 })
}

export const generateRandom = (length?: number) => (Math.random() + 1).toString(36).substring(length ?? 7)

export const base64ImgRexExp = /data:image\/[bmp,gif,ico,jpg,png,svg,webp,x\-icon,svg+xml]+;base64,[a-zA-Z0-9,+,/]+={0,2}/gm

export const setCookie = (cname: string, cvalue: string, exdays?: number, path: string = "/") => {
  if (getCookie(cname) !== "") { deleteCookie(cname) }
  if (!exdays) { exdays = 365 }

  const cookieDate = new Date()
  cookieDate.setTime(cookieDate.getTime() + (exdays * 24 * 60 * 60 * 1000))
  document.cookie = `${cname}=${cvalue};expires=${cookieDate.toUTCString()};path=${path}`
}

export const getCookie = (cname: string) => {
  const name = cname + "="
  const decodedCookie = decodeURIComponent(document.cookie)
  const cookiesTab = decodedCookie.split(';')

  for (let i = 0; i < cookiesTab.length; i++) {
    let cookie = cookiesTab[i]
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1)
    }
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length).trim()
    }
  }
  return ""
}

export const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

export const getBase64DataURI = async (file: File): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}

export const urlBase64ToUint8Array = (base64String: string) => {
  if (!base64String || typeof base64String !== 'string') {
    throw new Error('Invalid base64 string provided')
  }

  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  try {
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  } catch (error) {
    throw new Error(`Failed to decode base64 string: ${error}`)
  }
}

/**
 * Valide le format d'une clé VAPID publique
 * @param vapidKey - Clé VAPID à valider
 * @returns true si la clé est valide, false sinon
 */
export const isValidVapidKey = (vapidKey: string): boolean => {
  if (!vapidKey || typeof vapidKey !== 'string') {
    return false
  }

  // Une clé VAPID publique doit être une chaîne base64url d'environ 87 caractères
  if (vapidKey.length < 80 || vapidKey.length > 100) {
    return false
  }

  // Vérifier que la chaîne ne contient que des caractères base64url valides
  const base64urlRegex = /^[A-Za-z0-9_-]+$/
  return base64urlRegex.test(vapidKey)
}

/**
 * Convertit un ArrayBuffer en chaîne base64
 * @param buffer - ArrayBuffer à convertir
 * @returns Chaîne base64
 */
export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

/**
 * Convertit une chaîne base64 en ArrayBuffer
 * @param base64 - Chaîne base64 à convertir
 * @returns ArrayBuffer
 */
export const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

export const capitalize = (s = '') => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

export const isPhoneNumber = (phoneNumber: string | number) => {
  if ((typeof phoneNumber !== "string" && typeof phoneNumber !== "number")) return false
  return phoneNumber?.toString()?.match(/\d/g)?.length! >= 6
}

export const isEmail = (email: string) => {
  return /^[a-zA-Z0-9._-]+@[a-z0-9._-]{2,}\.[a-z]{2,4}$/gi.test(email)
}

export const formatNumber = (number: string | number) => {
  if (!number) return ""
  const num = number.toString()
  let counter = 0, result = ''
  for (let i = num.length - 1; i >= 0; i--) {
    if (counter !== 0 && counter % 3 === 0 && num[i] !== '+') {
      result = num[i] + ' ' + result
    } else {
      result = num[i] + result
    }
    counter++
  }
  return result
}

export const isValidUrl = (string: string) => {
  try {
    new URL(string)
    return true
  } catch (e) {
    return false
  }
}

export const isValidVCard = (text: string) => {
  if (!text || typeof text !== 'string') return false
  const trimmed = text.trim()
  const basicMatch = VCARD_REGEX.test(trimmed)
  if (!basicMatch) return false
  const hasBegin = /BEGIN:VCARD/i.test(trimmed);
  const hasEnd = /END:VCARD/i.test(trimmed);
  const hasVersion = /VERSION:[2-4]\.[0-9]/i.test(trimmed);
  return hasBegin && hasEnd && hasVersion;
}

export const getBase64MimeType = (b64String = '') => {
  let mimeType: string | undefined
  Object.entries(mimeTypesSignatures).some(([signature, type]) => {
    const signatureWasFound = b64String.startsWith(signature)
    if (signatureWasFound) mimeType = type
    return signatureWasFound
  })
  return mimeType
}

export const getBase64Extension = (b64String = '') => {
  if (/image\/jpg/gim.test(b64String)) return 'jpg'
  if (/image\/jpeg/gim.test(b64String)) return 'jpeg'
  if (/image\/png/gim.test(b64String)) return 'png'
  if (/image\/svg/gim.test(b64String)) return 'svg'
  if (/image\/webp/gim.test(b64String)) return 'webp'
  return ''
}

export const isEmptyObject = (obj: any) => {
  if (!obj) return true
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) return false
  }
  return true
}

export const tryParseJSON = (dataString: string) => {
  try {
    const parsedObject = JSON.parse(dataString)
    if (parsedObject && typeof parsedObject === "object") return parsedObject
  } catch (e) { }
  return false
}

export async function writeClipboardText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) { }
}

export const scrollPageTo = (x = 0, y = 0) => {
  (window.scroll || window.scrollTo || (() => { }))(x, y)
}

export const getClientInfos = () => {
  const client = {
    os: "",
    model: "",
    device: "",
    navigator: "",
    userAgent: navigator.userAgent
  }

  const ua = navigator.userAgent.toLowerCase()
  const platform = (navigator.platform || "").toLowerCase()
  const matches = (pattern: RegExp) => pattern.test(ua)

  if (matches(/msie|trident/)) client.navigator = "Internet Explorer"
  else if (matches(/edg/)) client.navigator = "Microsoft EDGE"
  else if (matches(/chrome/) && !matches(/edg|opr|brave/)) client.navigator = "Google Chrome"
  else if (matches(/firefox/)) client.navigator = "Mozilla Firefox"
  else if (matches(/opr/) || matches(/opera/)) client.navigator = "Opera"
  else if (matches(/safari/) && !matches(/chrome|edg|opr/)) client.navigator = "Safari"

  if ((navigator as any).brave && typeof (navigator as any).brave.isBrave === "function") {
    client.navigator = "Brave"
  }

  if (matches(/iphone/)) client.model = "iPhone"
  else if (matches(/ipad/)) client.model = "iPad"
  else if (matches(/ipod/)) client.model = "iPod"
  else if (matches(/android/) && matches(/mobile/)) client.model = "Android Phone"
  else if (matches(/android/) && !matches(/mobile/)) client.model = "Android Tablet"
  else if (matches(/blackberry/)) client.model = "Blackberry"
  else if (matches(/windows phone/)) client.model = "Windows Phone"
  else if (matches(/windows/) && matches(/touch/)) client.model = "Windows Tablet"

  const isMobile = matches(/android|webos|iphone|ipod|blackberry|iemobile|opera mini/)
  const isTablet = matches(/ipad/) || (matches(/android/) && !matches(/mobile/)) || (matches(/windows/) && matches(/touch/))

  if (isTablet) client.device = "Tablet"
  else if (isMobile) client.device = "Mobile"
  else client.device = "Desktop"

  if (matches(/linux/) || /linux/i.test(platform)) client.os = "Linux"
  if (matches(/windows/) || /win/i.test(platform)) client.os = "Windows"
  if ((matches(/macintosh|mac os x/) || /mac/i.test(platform)) && !matches(/ios/)) client.os = "Mac OS"
  if (matches(/iphone|ipad|ipod/) || matches(/ios/) || /ios/i.test(platform)) client.os = "IOS"
  if (matches(/android/)) client.os = "Android"

  return client
}

export const countryToFlag = (isoCode = '') => {
  if (isoCode === '') return ''
  return typeof String.fromCodePoint !== 'undefined'
    ? isoCode.toUpperCase().replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
    : isoCode
}
